import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, merge, switchMap } from 'rxjs';

import {
  NewEnquiryActions,
  UpdateEnquiryActions,
} from '@lockton-apps/enquiries/action';
import { ProposalDocumentBuilderActions } from '@lockton-apps/proposal-documents/action';
import { ClientApiActions } from '@lockton-apps/shared/action/clients';
import {
  ProposalsApiActions,
  ProposalsLocalActions,
} from '@lockton-apps/shared/action/proposals';
import { AuthUserSelectors } from '@lockton-apps/shared/data-access/auth-user-selectors';
import { SharedClientsSelectors } from '@lockton-apps/shared/data-access/clients';
import { ClientEnquiriesActions } from '@lockton-apps/clients/actions';

import { ProposalsService } from '../../services/proposals.service';

@Injectable()
export class ProposalConfigEffects {
  private store = inject(Store);
  private actions$ = inject(Actions);
  private proposalsService = inject(ProposalsService);

  loadConfig$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(ProposalDocumentBuilderActions.init),
        switchMap(() =>
          this.actions$.pipe(ofType(ProposalsApiActions.loadProposalSuccess))
        ),
        concatLatestFrom(() => [
          this.store
            .select(AuthUserSelectors.selectAuthenticatedUserLanguage)
            .pipe(filter(Boolean)),
          this.store.select(SharedClientsSelectors.selectRoutedClient),
        ]),
        map(([{ proposal }, locale, client]) => ({
          portalId: proposal.portalId,
          locale,
          clientType: client?.clientType,
        }))
      )
    ).pipe(
      switchMap(({ portalId, locale, clientType }) => {
        return this.proposalsService
          .getConfig(portalId, 1, locale, clientType)
          .pipe(
            map((config) => {
              return ProposalsLocalActions.getConfigSuccess({ config });
            })
          );
      })
    )
  );

  loadPartnerConfigs$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(NewEnquiryActions.init, UpdateEnquiryActions.init),
        switchMap(() =>
          this.actions$.pipe(
            ofType(
              ClientApiActions.loadClientSuccess,
              ClientApiActions.loadClientSuccessWithRelationshipChecks
            ),
            map(({ client }) => client)
          )
        )
      ),
      this.actions$.pipe(
        ofType(ClientEnquiriesActions.init),
        concatLatestFrom(() =>
          this.store.select(SharedClientsSelectors.selectRoutedClient)
        ),
        map(([, client]) => client),
        filter(Boolean)
      )
    ).pipe(
      concatLatestFrom(() =>
        this.store.select(AuthUserSelectors.selectAuthenticatedUser)
      ),
      switchMap(([client, user]) =>
        this.proposalsService
          .getPartnerConfigs(
            client.portalId,
            user?.language || 'en-GB',
            client.clientType
          )
          .pipe(
            map((configs) =>
              ProposalsLocalActions.getPartnerConfigsSuccess({ configs })
            )
          )
      )
    )
  );
}
